import type { Flight, FlightStatus } from '@/lib/api/types';
import { config } from '@/lib/config';
import { VOLUME_DASH, VOLUME_LINE_WIDTH, VOLUME_PALETTE } from '@/lib/map/volumeStyle';
import { useUiStore } from '@/lib/store/ui';
import mapboxgl from 'mapbox-gl';
import { useEffect, useRef } from 'react';

/** The flown/planned path. Matches the mockup's polyline and the app's
 *  "Active" green — the path is the aircraft, not the airspace. */
const PATH_COLOR = VOLUME_PALETTE.green;

/**
 * Volume style by flight status, in the app's existing airspace grammar
 * (see lib/map/volumeStyle.ts): colour = state, dash = how much the polygon
 * asserts about *now*.
 *
 * Deliberately NOT a fixed blue. `#2563eb` already means "Approved" in
 * lib/map/layers/oi.ts, so painting every flight's volume blue told the
 * operator a cancelled flight's airspace was approved. Keyed off the same
 * `flight_status` the table colours its rows with, so map and table agree.
 */
const VOLUME_STYLE: Record<FlightStatus, { color: string; dash: number[]; fill: number }> = {
  Active: { color: VOLUME_PALETTE.green, dash: [...VOLUME_DASH.solid], fill: 0.1 },
  Completed: { color: VOLUME_PALETTE.gray, dash: [...VOLUME_DASH.solid], fill: 0.07 },
  Aborted: { color: VOLUME_PALETTE.red, dash: [...VOLUME_DASH.tight], fill: 0.13 },
  Planned: { color: VOLUME_PALETTE.amber, dash: [...VOLUME_DASH.medium], fill: 0.12 },
};

const SRC_TRACK = 'flight-track';
const SRC_VOLUME = 'flight-volume';
const LYR_TRACK = 'flight-track-line';
const LYR_VOLUME_FILL = 'flight-volume-fill';
const LYR_VOLUME_LINE = 'flight-volume-line';

/** The line the map draws, in Mapbox `[lng, lat]` order.
 *  The flown track when telemetry has loaded, otherwise the planned
 *  waypoints (already flipped by the mapper). Points with no position are
 *  dropped rather than defaulted — a null coordinate is not 0,0. */
function lineFor(flight: Flight): Array<[number, number]> {
  const track = flight.track;
  if (track && track.length > 0) {
    const line: Array<[number, number]> = [];
    for (const p of track) {
      if (p.lon != null && p.lat != null) line.push([p.lon, p.lat]);
    }
    if (line.length > 0) return line;
  }
  return (flight.waypoints ?? []).map((w) => [w[0], w[1]]);
}

/**
 * Per-flight map: the flown track (demo) or the planned waypoints plus the
 * operational volume and last-known position (live).
 *
 * Owns its OWN `mapboxgl.Map` — not the global singleton in
 * `lib/map/instance.ts`, which belongs to `MapCanvas` and carries none of
 * these layers. Same approach as `useDsMap`.
 *
 * The caller keys this component by `flight.flight_id`, so one mount ==
 * one flight and the setup effect can safely run once. That key is stable
 * across query refetches, which is the point: `flight` gets a fresh object
 * identity every time the flights query refetches (60s staleTime + refetch
 * on focus), and rebuilding the map on that cadence is a visible flicker.
 * The flight is therefore read through a ref rather than a dependency.
 */
/** Names the two geometries. Without it the path and its buffered volume read
 *  as one duplicated outline — they sit within a fixed lateral buffer of each
 *  other by construction, so they will nearly always overlap. */
function Legend({ flight }: { flight: Flight }) {
  const vs = VOLUME_STYLE[flight.flight_status];
  const flown = (flight.track?.length ?? 0) > 0;
  const hasPath = flown || (flight.waypoints?.length ?? 0) > 0;
  if (!hasPath && !flight.operational_volume) return null;

  return (
    <div className="absolute bottom-2 left-2 z-10 rounded-md border border-(--color-border) bg-(--color-surface)/90 px-2 py-1.5 space-y-1 backdrop-blur-sm pointer-events-none">
      {hasPath && (
        <div className="flex items-center gap-1.5 text-[0.615em] text-(--color-text2)">
          <span className="h-0 w-4 shrink-0 border-t-2" style={{ borderColor: PATH_COLOR }} />
          {flown ? 'Flown track' : 'Planned waypoints'}
        </div>
      )}
      {flight.operational_volume && (
        <div className="flex items-center gap-1.5 text-[0.615em] text-(--color-text2)">
          <span
            className="h-0 w-4 shrink-0 border-t-2"
            style={{
              borderColor: vs.color,
              borderTopStyle: vs.dash.length > 1 ? 'dashed' : 'solid',
            }}
          />
          Operational volume
        </div>
      )}
    </div>
  );
}

export function FlightMap({ flight, scrubIndex }: { flight: Flight; scrubIndex: number }) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const markerRef = useRef<mapboxgl.Marker | null>(null);
  /** Camera target awaiting a container with a real measured size. Cleared
   *  once applied — the fit happens exactly once, so it never fights a user
   *  who has panned or zoomed. */
  const pendingFitRef = useRef<Array<[number, number]> | null>(null);
  const flightRef = useRef(flight);
  flightRef.current = flight;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const f = flightRef.current;

    mapboxgl.accessToken = config.map.mapboxToken;
    const [lat, lng] = config.map.defaultCenter; // stored [lat, lng]; Mapbox wants [lng, lat]
    const map = new mapboxgl.Map({
      container,
      // Share the user's persisted basemap rather than forcing the default.
      style: useUiStore.getState().basemap,
      center: [lng, lat],
      zoom: config.map.defaultZoom,
      attributionControl: false,
    });

    /** Apply the pending camera fit, but ONLY once the container has a real
     *  measured size. `fitBounds` derives its zoom from the viewport, so
     *  fitting against a 0x0 or not-yet-laid-out container frames the
     *  geometry as a speck in a far-too-wide view. Re-invoked from the
     *  ResizeObserver so the fit lands the instant a real size appears. */
    const applyFit = () => {
      const pts = pendingFitRef.current;
      if (!pts || pts.length === 0) return;
      if (container.clientWidth === 0 || container.clientHeight === 0) return;

      const first = pts[0];
      if (!first) return;
      if (pts.length === 1) {
        map.setCenter(first);
        map.setZoom(14);
      } else {
        const bounds = pts.reduce((b, p) => b.extend(p), new mapboxgl.LngLatBounds(first, first));
        map.fitBounds(bounds, { padding: 24, duration: 0 });
      }
      pendingFitRef.current = null;
    };

    const draw = () => {
      // Sync the viewport BEFORE any camera work. `fitBounds` chooses a zoom
      // that makes the bounds fit the size Mapbox currently believes it has;
      // if the container was measured small (or not yet laid out) at
      // construction, fitting first and resizing after leaves the geometry a
      // speck in a far-too-wide view. Whether that happened depended on
      // whether the style loaded before the rAF resize below — which is why
      // it reproduced on some flights and not others.
      map.resize();

      const line = lineFor(f);
      const volume = f.operational_volume;

      if (line.length > 1) {
        map.addSource(SRC_TRACK, {
          type: 'geojson',
          data: {
            type: 'Feature',
            properties: {},
            geometry: { type: 'LineString', coordinates: line },
          },
        });
        map.addLayer({
          id: LYR_TRACK,
          type: 'line',
          source: SRC_TRACK,
          paint: { 'line-color': PATH_COLOR, 'line-width': 3 },
        });
      }

      if (volume) {
        const vs = VOLUME_STYLE[f.flight_status];
        map.addSource(SRC_VOLUME, {
          type: 'geojson',
          data: { type: 'Feature', properties: {}, geometry: volume },
        });
        map.addLayer({
          id: LYR_VOLUME_FILL,
          type: 'fill',
          source: SRC_VOLUME,
          paint: { 'fill-color': vs.color, 'fill-opacity': vs.fill },
        });
        map.addLayer({
          id: LYR_VOLUME_LINE,
          type: 'line',
          source: SRC_VOLUME,
          paint: {
            'line-color': vs.color,
            'line-width': VOLUME_LINE_WIDTH,
            'line-dasharray': vs.dash,
          },
        });
      }

      // Marker: the scrub cursor when there is a track, otherwise the
      // last-known position — the only positional fact live can offer for a
      // historical flight.
      const lastKnown = f.last_known;
      const start: [number, number] | null =
        line[0] ?? (lastKnown ? [lastKnown.lon, lastKnown.lat] : null);
      if (start) {
        const el = document.createElement('div');
        el.style.cssText = `width:14px;height:14px;border-radius:50%;background:${PATH_COLOR};border:2px solid #0a2e17`;
        markerRef.current = new mapboxgl.Marker({ element: el }).setLngLat(start).addTo(map);
      }

      // Fit to whatever geometry exists — track, then waypoints, then the
      // volume ring, then the single last-known point.
      pendingFitRef.current = [
        ...line,
        ...(volume?.coordinates.flat() ?? []),
        ...(lastKnown ? [[lastKnown.lon, lastKnown.lat] as [number, number]] : []),
      ];
      applyFit();
    };

    map.once('load', draw);

    // Mapbox measures the container ONCE, in the constructor. If it reports
    // 0x0 at that instant the map renders nothing and never re-measures on
    // its own — a permanently blank pane. The rAF pass covers the initial
    // mount and the observer covers later layout changes (this pane is
    // responsive: it moves from a column to a row at `lg`, and the scrubber
    // below it appears/disappears per flight). Same guard as `useDsMap`.
    const ro = new ResizeObserver(() => {
      map.resize();
      applyFit();
    });
    ro.observe(container);
    const raf = requestAnimationFrame(() => {
      map.resize();
      applyFit();
    });

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      markerRef.current?.remove();
      markerRef.current = null;
      map.remove();
    };
  }, []);

  // Move the marker with the scrubber — touches only the marker, never the
  // map instance or its layers.
  useEffect(() => {
    const p = flightRef.current.track?.[scrubIndex];
    // A point with no position leaves the marker where it was — moving it to
    // 0,0 would put the aircraft in the Gulf of Guinea.
    if (!p || p.lon == null || p.lat == null || !markerRef.current) return;
    markerRef.current.setLngLat([p.lon, p.lat]);
  }, [scrubIndex]);

  const hasGeometry =
    (flight.track?.length ?? 0) > 0 ||
    (flight.waypoints?.length ?? 0) > 0 ||
    flight.operational_volume != null ||
    flight.last_known != null;

  if (!hasGeometry) {
    return (
      <div className="flex-1 min-h-[200px] flex items-center justify-center text-[0.846em] text-(--color-text3)">
        No position data for this flight.
      </div>
    );
  }

  return (
    // The container stays IN FLOW (`flex-1`) so it has a real measured size
    // the moment Mapbox reads it. Only the legend is taken out of flow.
    // Absolutely positioning the container itself is what blanked this map.
    <div className="relative flex-1 min-h-[200px] flex">
      <div ref={containerRef} className="flex-1" />
      <Legend flight={flight} />
    </div>
  );
}
